import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  HiUser,
  HiChartBar,
  HiDocumentText,
  HiCheckCircle,
  HiClock,
  HiArrowDownTray,
  HiPrinter,
  HiSparkles,
  HiRocketLaunch,
  HiDocumentChartBar,
} from 'react-icons/hi2';
import {
  generateMonthlyCoachingReport,
  generateCoachMonthlyReports,
  exportReportToJSON,
  formatReportForDisplay,
} from '../../services/coachingReportService';
import { useAuth } from '../../context/AuthContext';
import { useUserRole } from '../../context/UserRoleContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import toast from 'react-hot-toast';

const MonthlyCoachingReport = ({ userRole: propUserRole, clientId: propClientId = null }) => {
  const { currentUser } = useAuth();
  const { userRole: contextUserRole } = useUserRole();
  const effectiveUserRole = propUserRole || contextUserRole || 'coach';

  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [reports, setReports] = useState([]); // For multiple client reports
  const [selectedClient, setSelectedClient] = useState(propClientId || null);
  const [clients, setClients] = useState([]);
  const [clientLookup, setClientLookup] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const isCoach = effectiveUserRole === 'coach' || effectiveUserRole === 'businessCoach';
  const isAdmin = effectiveUserRole === 'admin' || effectiveUserRole === 'moderator';
  const canViewAllClients = isAdmin || effectiveUserRole === 'Business Advisor';

  // Fetch clients list
  useEffect(() => {
    const fetchClients = async () => {
      if (!currentUser?.email) return;

      try {
        let clientsQuery;
        if (canViewAllClients) {
          // Admin/Business Advisor can see all clients
          clientsQuery = query(collection(firestore, 'clients'));
        } else if (isCoach) {
          // Coach sees only assigned clients
          clientsQuery = query(
            collection(firestore, 'clients'),
            where('assignedEmail', '==', currentUser.email)
          );
        } else {
          return;
        }

        const snapshot = await getDocs(clientsQuery);
        const clientsList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setClients(clientsList);
        setClientLookup(
          clientsList.reduce((acc, client) => {
            acc[client.id] = client;
            return acc;
          }, {})
        );

        // Auto-select first client if none selected
        if (!selectedClient && clientsList.length > 0) {
          setSelectedClient(clientsList[0].id);
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
        toast.error('Failed to load clients');
      }
    };

    fetchClients();
  }, [currentUser?.email, canViewAllClients, isCoach, selectedClient]);

  // Generate report for single client
  const handleGenerateReport = async () => {
    if (!selectedClient) {
      toast.error('Please select a client');
      return;
    }

    setLoading(true);
    try {
      const generatedReport = await generateMonthlyCoachingReport({
        clientId: selectedClient,
        month: selectedMonth,
        year: selectedYear,
        coachId: isCoach ? currentUser?.email : null,
      });
      setReport(prepareReportForUI(generatedReport));
      setReports([]); // Clear multiple reports
      toast.success('Report generated successfully');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(error.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  // Generate reports for all assigned clients (coach view)
  const handleGenerateAllReports = async () => {
    if (!isCoach) return;

    setLoading(true);
    try {
      const generatedReports = await generateCoachMonthlyReports({
        coachEmail: currentUser?.email,
        month: selectedMonth,
        year: selectedYear,
      });
      setReports(generatedReports.map(prepareReportForUI));
      setReport(null); // Clear single report
      toast.success(`Generated ${generatedReports.length} reports`);
    } catch (error) {
      console.error('Error generating reports:', error);
      toast.error(error.message || 'Failed to generate reports');
    } finally {
      setLoading(false);
    }
  };

  const handleExportJSON = () => {
    const data = report || reports;
    if (!data) {
      toast.error('No report to export');
      return;
    }

    const jsonString = exportReportToJSON(data);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `coaching-report-${selectedMonth}-${selectedYear}${report ? `-${report.clientInfo.name.replace(/\s+/g, '-')}` : '-all'}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  const handlePrint = () => {
    window.print();
  };

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const getScoreColor = score => {
    if (score >= 85) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = score => {
    if (score >= 85) return 'bg-green-600';
    if (score >= 60) return 'bg-yellow-600';
    return 'bg-red-600';
  };

  const describeScore = score => {
    if (score === null || score === undefined) return 'No data yet';
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Stable';
    if (score >= 40) return 'Developing';
    if (score > 0) return 'At Risk';
    return 'Not started';
  };

  const formatUpdatedOn = value => {
    if (!value) return 'Not updated';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return 'Not updated';
    return parsed.toLocaleDateString();
  };

  const getOrganizationFromClient = client =>
    client?.organization ||
    client?.organisation ||
    client?.organizationName ||
    client?.organisationName ||
    client?.companyName ||
    client?.businessName ||
    '';

  const getClientDisplayName = client => {
    if (!client) return 'Unnamed client';
    const organization = getOrganizationFromClient(client);
    if (organization) return organization;
    const fullName =
      `${client.firstname || ''} ${client.lastname || ''}`.trim() ||
      client.fullname ||
      client.displayName ||
      '';
    return fullName || client.email || client.name || 'Unnamed client';
  };

  const getClientNameFromRecord = client =>
    `${client?.firstname || ''} ${client?.lastname || ''}`.trim() ||
    client?.contactName ||
    client?.displayName ||
    client?.name ||
    '';

  const getOrganizationFromCache = clientId => {
    if (!clientId) return '';
    const matchedClient = clientLookup[clientId];
    if (!matchedClient) return '';
    return getOrganizationFromClient(matchedClient);
  };

  const getClientNameFromCache = clientId => {
    if (!clientId) return '';
    const matchedClient = clientLookup[clientId];
    if (!matchedClient) return '';
    return getClientNameFromRecord(matchedClient);
  };

  const enhanceReportWithClientMeta = rawReport => {
    if (!rawReport) return rawReport;
    const clientId = rawReport.reportMetadata?.clientId;
    const orgFromCache = getOrganizationFromCache(clientId);
    const nameFromCache = getClientNameFromCache(clientId);

    return {
      ...rawReport,
      clientInfo: {
        ...rawReport.clientInfo,
        name:
          rawReport.clientInfo?.name ||
          nameFromCache ||
          getOrganizationFromClient(rawReport.clientInfo) ||
          rawReport.clientInfo?.email ||
          'Client',
        organization: rawReport.clientInfo?.organization || orgFromCache || '',
      },
    };
  };

  const prepareReportForUI = rawReport => {
    if (!rawReport) return rawReport;
    return formatReportForDisplay(enhanceReportWithClientMeta(rawReport));
  };

  useEffect(() => {
    if (!report && reports.length === 0) return;
    setReport(prev => (prev ? prepareReportForUI(prev) : prev));
    setReports(prev => (prev.length ? prev.map(prepareReportForUI) : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientLookup]);

  const readinessChecklist = report?.commercialization?.readinessChecklist || null;
  const gtmPlan = report?.commercialization?.gtmPlan || null;
  const launchChecklist = report?.commercialization?.launchChecklist || null;
  const entrepreneurProfile = report?.entrepreneurProfile || null;
  const businessHealth = report?.businessHealth || null;

  const formatPercent = value =>
    typeof value === 'number' && !Number.isNaN(value) ? `${value}%` : '—';

  const percentColor = value =>
    typeof value === 'number' && !Number.isNaN(value) ? getScoreColor(value) : 'text-gray-400';

  const focusStats = report
    ? [
        {
          id: 'commercialisation',
          title: 'Commercialisation Readiness',
          value: report.statistics.readinessScore ?? null,
          detail:
            readinessChecklist?.readinessLevel || describeScore(report.statistics.readinessScore),
          icon: HiDocumentText,
        },
        {
          id: 'gtm',
          title: 'Go-To-Market Plan',
          value: report.statistics.gtmProgress ?? null,
          detail: gtmPlan
            ? `${gtmPlan.stepsCompleted || 0}/${gtmPlan.totalSteps || 0} steps complete`
            : 'No plan submitted',
          icon: HiRocketLaunch,
        },
        {
          id: 'launch',
          title: 'Startup Launch Check',
          value: report.statistics.launchScore ?? null,
          detail: launchChecklist
            ? `${launchChecklist.totalChecked || 0}/${launchChecklist.totalItems || 0} checkpoints`
            : 'Not started',
          icon: HiCheckCircle,
        },
        {
          id: 'profiling',
          title: 'Entrepreneur Profiling',
          value: entrepreneurProfile?.overallScore ?? null,
          detail: entrepreneurProfile?.status || 'Awaiting assessment',
          icon: HiUser,
        },
        {
          id: 'health',
          title: 'Business Health Check',
          value: businessHealth?.overallScore ?? null,
          detail: businessHealth?.status || 'Awaiting assessment',
          icon: HiChartBar,
        },
      ]
    : [];

  const moduleDeepDive = report
    ? [
        {
          key: 'commercialisation',
          title: 'Commercialisation Readiness',
          track: 'Commercialisation',
          icon: HiDocumentText,
          subtitle: 'Checklist progress captured inside Commercialisation workspace',
          metrics: [
            {
              label: 'Score',
              value: formatPercent(readinessChecklist?.score),
              helper: readinessChecklist?.readinessLevel || 'Awaiting checklist',
            },
            {
              label: 'Checklist',
              value: readinessChecklist
                ? `${readinessChecklist.totalChecked || 0}/${readinessChecklist.totalItems || 0}`
                : '0/0',
              helper: 'Items complete',
            },
            {
              label: 'Last update',
              value: readinessChecklist?.lastUpdated
                ? formatUpdatedOn(readinessChecklist.lastUpdated)
                : '—',
              helper: '',
            },
          ],
        },
        {
          key: 'gtm',
          title: 'Go-To-Market Strategy',
          track: 'Go To Market',
          icon: HiRocketLaunch,
          subtitle: 'Execution heartbeat taken from GTM Strategy Plan stored in Firestore',
          metrics: [
            {
              label: 'Progress',
              value: formatPercent(gtmPlan?.progress),
              helper: gtmPlan
                ? `${gtmPlan.stepsCompleted || 0}/${gtmPlan.totalSteps || 0} steps`
                : 'No plan recorded',
            },
            {
              label: 'Status',
              value: gtmPlan ? (gtmPlan.isDraft ? 'Draft' : 'Ready') : 'Not started',
              helper: gtmPlan?.currentStep ? `Currently on step ${gtmPlan.currentStep}` : '',
            },
            {
              label: 'Last update',
              value: gtmPlan?.lastUpdated ? formatUpdatedOn(gtmPlan.lastUpdated) : '—',
              helper: '',
            },
          ],
        },
        {
          key: 'launch',
          title: 'Startup Launch Check',
          track: 'Startup Launch',
          icon: HiCheckCircle,
          subtitle: 'Launch checklist completion across product, team, and compliance gates',
          metrics: [
            {
              label: 'Readiness',
              value: formatPercent(launchChecklist?.score),
              helper: launchChecklist?.readinessLevel || 'Awaiting review',
            },
            {
              label: 'Checkpoints',
              value: launchChecklist
                ? `${launchChecklist.totalChecked || 0}/${launchChecklist.totalItems || 0}`
                : '0/0',
              helper: 'Completed vs total',
            },
            {
              label: 'Last update',
              value: launchChecklist?.lastUpdated
                ? formatUpdatedOn(launchChecklist.lastUpdated)
                : '—',
              helper: '',
            },
          ],
        },
        {
          key: 'entrepreneur',
          title: 'Entrepreneur Profiling',
          track: 'Entrepreneur Profiling',
          icon: HiUser,
          subtitle: 'Personality, mindset, and capability data sourced from profiling module',
          metrics: [
            {
              label: 'Overall score',
              value: formatPercent(entrepreneurProfile?.overallScore),
              helper: entrepreneurProfile?.status || 'Awaiting assessment',
            },
            {
              label: 'Focus areas',
              value: entrepreneurProfile?.focusAreas?.length
                ? entrepreneurProfile.focusAreas.length
                : '—',
              helper: entrepreneurProfile?.focusAreas?.join(', '),
            },
            {
              label: 'Last update',
              value: entrepreneurProfile?.lastUpdated
                ? formatUpdatedOn(entrepreneurProfile.lastUpdated)
                : '—',
              helper: '',
            },
          ],
        },
        {
          key: 'businessHealth',
          title: 'Business Healthcheck',
          track: 'Business Health',
          icon: HiChartBar,
          subtitle: 'Operational resilience snapshot based on Business Health assessment',
          metrics: [
            {
              label: 'Overall health',
              value: formatPercent(businessHealth?.overallScore),
              helper: businessHealth?.status || 'Awaiting assessment',
            },
            {
              label: 'Risks flagged',
              value: businessHealth?.focusAreas?.length ? businessHealth.focusAreas.length : '—',
              helper: businessHealth?.focusAreas?.join(', '),
            },
            {
              label: 'Last update',
              value: businessHealth?.lastUpdated
                ? formatUpdatedOn(businessHealth.lastUpdated)
                : '—',
              helper: '',
            },
          ],
        },
      ]
    : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-black">Monthly Coaching Reports</h1>
              <p className="mt-2 text-gray-600">
                Generate comprehensive monthly reports with all key client information and platform
                data
              </p>
            </div>
            <div className="flex items-center gap-3">
              {report && (
                <>
                  <button
                    onClick={handleExportJSON}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
                  >
                    <HiArrowDownTray className="h-4 w-4" />
                    Export JSON
                  </button>
                  <button
                    onClick={handlePrint}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
                  >
                    <HiPrinter className="h-4 w-4" />
                    Print
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Month</label>
              <select
                value={selectedMonth}
                onChange={e => setSelectedMonth(Number(e.target.value))}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {months.map((month, index) => (
                  <option key={index + 1} value={index + 1}>
                    {month}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Year</label>
              <select
                value={selectedYear}
                onChange={e => setSelectedYear(Number(e.target.value))}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {years.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            {canViewAllClients && (
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Client</label>
                <select
                  value={selectedClient || ''}
                  onChange={e => setSelectedClient(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">Select a client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {getClientDisplayName(client)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex items-end gap-2">
              <button
                onClick={handleGenerateReport}
                disabled={loading || (canViewAllClients && !selectedClient)}
                className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? 'Generating...' : 'Generate Report'}
              </button>
              {isCoach && clients.length > 0 && (
                <button
                  onClick={handleGenerateAllReports}
                  disabled={loading}
                  className="rounded-lg border border-blue-600 bg-white px-4 py-2 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
                  title="Generate reports for all assigned clients"
                >
                  All
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Single Report Display */}
        {report && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <section className="rounded-2xl border border-gray-200 bg-white/90 p-8 shadow-sm backdrop-blur">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Monthly Coaching Report · {report.reportMetadata.reportMonthName}
                  </p>
                  <h2 className="mt-2 text-3xl font-semibold text-black">
                    {report.clientInfo.name || getClientDisplayName(report.clientInfo)}
                  </h2>
                  <p className="text-gray-600">
                    {report.clientInfo.organization || 'Organization not captured'}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {report.clientInfo.businessStage && (
                      <span className="rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-gray-700">
                        {report.clientInfo.businessStage}
                      </span>
                    )}
                    {report.clientInfo.status && (
                      <span
                        className={`rounded-full border px-3 py-1 text-xs font-medium ${
                          report.clientInfo.status === 'active'
                            ? 'border-green-100 bg-green-50 text-green-700'
                            : 'border-gray-200 bg-gray-100 text-gray-700'
                        }`}
                      >
                        {report.clientInfo.status}
                      </span>
                    )}
                  </div>
                </div>
                <div className="rounded-2xl border border-gray-100 bg-gray-50/80 p-4 text-sm text-gray-700">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500">Generated</p>
                      <p className="font-semibold text-gray-900">{report.formattedDate}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500">
                        Assigned Coach
                      </p>
                      <p className="font-semibold text-gray-900">
                        {report.clientInfo.assignedCoach || 'Not assigned'}
                      </p>
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-gray-500">
                    Client since {report.clientInfo.joinedDate || 'Unknown'}
                  </p>
                </div>
              </div>
              <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Email</p>
                  <p className="mt-1 text-gray-900">{report.clientInfo.email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Phone</p>
                  <p className="mt-1 text-gray-900">{report.clientInfo.phone || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Location</p>
                  <p className="mt-1 text-gray-900">{report.clientInfo.location || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Organization</p>
                  <p className="mt-1 text-gray-900">{report.clientInfo.organization || 'N/A'}</p>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  {
                    label: 'Organization',
                    value: report.clientInfo.organization || 'Not captured',
                  },
                  {
                    label: 'Business Stage',
                    value: report.clientInfo.businessStage || 'Not provided',
                  },
                  {
                    label: 'Assigned Coach',
                    value: report.clientInfo.assignedCoach || 'Pending assignment',
                  },
                  { label: 'Client Since', value: report.clientInfo.joinedDate || 'Unknown' },
                ].map(item => (
                  <div
                    key={item.label}
                    className="rounded-2xl border border-gray-100 bg-gray-50/70 p-4 text-sm text-gray-600"
                  >
                    <p className="text-xs uppercase tracking-wide text-gray-500">{item.label}</p>
                    <p className="mt-1 text-base font-semibold text-gray-900">{item.value}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-black">Strategic Focus KPIs</h3>
                <p className="text-sm text-gray-600">
                  Commercialisation, Go-To-Market, Launch, Profiling, and Business Health
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
                {focusStats.map(stat => (
                  <motion.div
                    key={stat.id}
                    whileHover={{ y: -3, scale: 1.01 }}
                    className="rounded-2xl border border-gray-200 bg-white/90 p-5 shadow-sm backdrop-blur"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                        <p
                          className={`mt-2 text-3xl font-semibold ${
                            stat.value !== null ? getScoreColor(stat.value) : 'text-gray-400'
                          }`}
                        >
                          {stat.value !== null ? `${stat.value}%` : '—'}
                        </p>
                        <p className="text-xs text-gray-500">{stat.detail}</p>
                      </div>
                      <div className="rounded-full bg-blue-50 p-3 text-blue-600">
                        <stat.icon className="h-5 w-5" />
                      </div>
                    </div>
                    <div className="mt-4 h-2 w-full rounded-full bg-gray-100">
                      <div
                        className={`h-full rounded-full ${
                          stat.value !== null ? getScoreBgColor(stat.value) : 'bg-gray-300'
                        }`}
                        style={{ width: `${stat.value !== null ? stat.value : 0}%` }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </section>

            <section className="rounded-2xl border border-gray-200 bg-white/90 p-6 shadow-sm backdrop-blur">
              <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Module Deep Dive</p>
                  <h3 className="text-xl font-semibold text-black">Commercialisation Journey</h3>
                  <p className="text-sm text-gray-600">
                    Data is stitched live from Commercialisation, Go To Market, Launch, Entrepreneur
                    Profiling, and Business Healthcheck modules stored in Firestore.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {moduleDeepDive.map(module => (
                  <motion.div
                    key={module.key}
                    whileHover={{ y: -4 }}
                    className="flex h-full flex-col rounded-2xl border border-gray-100 bg-gray-50/80 p-5 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500">
                          {module.track}
                        </p>
                        <h4 className="text-lg font-semibold text-gray-900">{module.title}</h4>
                      </div>
                      <div className="rounded-full bg-white p-3 text-blue-600 shadow-sm">
                        <module.icon className="h-5 w-5" />
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-gray-600">{module.subtitle}</p>
                    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {module.metrics.map(metric => (
                        <div
                          key={`${module.key}-${metric.label}`}
                          className="rounded-xl border border-white bg-white/70 p-4"
                        >
                          <p className="text-xs uppercase tracking-wide text-gray-500">
                            {metric.label}
                          </p>
                          <p className="mt-1 text-2xl font-semibold text-gray-900">
                            {metric.value || '—'}
                          </p>
                          {metric.helper && (
                            <p className="text-xs text-gray-500">{metric.helper}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </section>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl border border-gray-200 bg-white/90 p-6 shadow-sm backdrop-blur"
              >
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">Execution</p>
                    <h3 className="text-xl font-semibold text-black">Commercialisation & Launch</h3>
                  </div>
                  <HiRocketLaunch className="h-6 w-6 text-blue-600" />
                </div>
                <div className="space-y-4">
                  {readinessChecklist ? (
                    <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Readiness Checklist</p>
                          <p className="text-2xl font-semibold text-gray-900">
                            {readinessChecklist.score || 0}%
                          </p>
                        </div>
                        <span className="text-xs font-medium text-gray-500">
                          {readinessChecklist.readinessLevel ||
                            describeScore(readinessChecklist.score)}
                        </span>
                      </div>
                      <div className="mt-3 h-2 w-full rounded-full bg-white">
                        <div
                          className={`h-full rounded-full ${getScoreBgColor(readinessChecklist.score || 0)}`}
                          style={{ width: `${readinessChecklist.score || 0}%` }}
                        />
                      </div>
                      <p className="mt-2 text-xs text-gray-500">
                        {readinessChecklist.totalChecked || 0}/{readinessChecklist.totalItems || 0}{' '}
                        items complete
                      </p>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-gray-200 p-4 text-sm text-gray-500">
                      No commercialisation readiness data yet.
                    </div>
                  )}

                  {gtmPlan ? (
                    <div className="rounded-xl border border-gray-100 bg-white p-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-600">Go-To-Market Plan</p>
                        <span className="text-xs text-gray-500">
                          {gtmPlan.stepsCompleted || 0}/{gtmPlan.totalSteps || 0} steps
                        </span>
                      </div>
                      <p
                        className={`mt-2 text-2xl font-semibold ${getScoreColor(gtmPlan.progress || 0)}`}
                      >
                        {gtmPlan.progress || 0}%
                      </p>
                      <div className="mt-3 h-2 w-full rounded-full bg-gray-100">
                        <div
                          className={`h-full rounded-full ${getScoreBgColor(gtmPlan.progress || 0)}`}
                          style={{ width: `${gtmPlan.progress || 0}%` }}
                        />
                      </div>
                      <p className="mt-2 text-xs text-gray-500">
                        {gtmPlan.isDraft ? 'Draft in progress' : 'Ready for review'}
                      </p>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-gray-200 p-4 text-sm text-gray-500">
                      No GTM plan has been submitted.
                    </div>
                  )}

                  {launchChecklist ? (
                    <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-600">Startup Launch Check</p>
                        <span className="text-xs text-gray-500">
                          {launchChecklist.totalChecked || 0}/{launchChecklist.totalItems || 0}{' '}
                          checkpoints
                        </span>
                      </div>
                      <p
                        className={`mt-2 text-2xl font-semibold ${getScoreColor(launchChecklist.score || 0)}`}
                      >
                        {launchChecklist.score || 0}%
                      </p>
                      <div className="mt-3 h-2 w-full rounded-full bg-white">
                        <div
                          className={`h-full rounded-full ${getScoreBgColor(launchChecklist.score || 0)}`}
                          style={{ width: `${launchChecklist.score || 0}%` }}
                        />
                      </div>
                      <p className="mt-2 text-xs text-gray-500">
                        {launchChecklist.readinessLevel || describeScore(launchChecklist.score)}
                      </p>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-gray-200 p-4 text-sm text-gray-500">
                      Launch checklist not yet started.
                    </div>
                  )}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl border border-gray-200 bg-white/90 p-6 shadow-sm backdrop-blur"
              >
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">People</p>
                    <h3 className="text-xl font-semibold text-black">Entrepreneur Profiling</h3>
                  </div>
                  <HiUser className="h-6 w-6 text-blue-600" />
                </div>
                {entrepreneurProfile ? (
                  <>
                    <div className="rounded-xl border border-blue-50 bg-blue-50/70 p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-blue-800">Overall profile score</p>
                          <p className="text-3xl font-semibold text-blue-900">
                            {entrepreneurProfile.overallScore || 0}%
                          </p>
                          <p className="text-xs text-blue-700">{entrepreneurProfile.status}</p>
                        </div>
                        <div className="text-right text-xs text-blue-700">
                          <p>Updated</p>
                          <p className="font-semibold">
                            {formatUpdatedOn(entrepreneurProfile.lastUpdated)}
                          </p>
                        </div>
                      </div>
                      {entrepreneurProfile.focusAreas?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {entrepreneurProfile.focusAreas.map(area => (
                            <span
                              key={area}
                              className="rounded-full bg-white/80 px-3 py-1 text-xs font-medium text-amber-600"
                            >
                              Needs work: {area}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="mt-5 space-y-3">
                      {entrepreneurProfile.categories.map(category => (
                        <div key={category.key} className="rounded-lg border border-gray-100 p-3">
                          <div className="flex items-center justify-between text-sm font-medium text-gray-700">
                            <span>{category.label}</span>
                            <span>{category.score ? `${category.score}%` : '—'}</span>
                          </div>
                          <div className="mt-2 h-2 w-full rounded-full bg-gray-100">
                            <div
                              className={`h-full rounded-full ${
                                category.score ? getScoreBgColor(category.score) : 'bg-gray-300'
                              }`}
                              style={{ width: `${category.score || 0}%` }}
                            />
                          </div>
                          {category.summary && (
                            <p className="mt-2 text-xs text-gray-500">{category.summary}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="rounded-xl border border-dashed border-gray-200 p-4 text-sm text-gray-500">
                    No entrepreneur profiling assessments recorded yet.
                  </div>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl border border-gray-200 bg-white/90 p-6 shadow-sm backdrop-blur"
              >
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">Health</p>
                    <h3 className="text-xl font-semibold text-black">Business Health Check</h3>
                  </div>
                  <HiChartBar className="h-6 w-6 text-blue-600" />
                </div>
                {businessHealth ? (
                  <>
                    <div className="rounded-xl border border-green-50 bg-green-50/70 p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-800">Overall health score</p>
                          <p className="text-3xl font-semibold text-green-900">
                            {businessHealth.overallScore || 0}%
                          </p>
                          <p className="text-xs text-green-700">{businessHealth.status}</p>
                        </div>
                        <div className="text-right text-xs text-green-700">
                          <p>Updated</p>
                          <p className="font-semibold">
                            {formatUpdatedOn(businessHealth.lastUpdated)}
                          </p>
                        </div>
                      </div>
                      {businessHealth.focusAreas?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {businessHealth.focusAreas.map(area => (
                            <span
                              key={area}
                              className="rounded-full bg-white/80 px-3 py-1 text-xs font-medium text-red-600"
                            >
                              Risk: {area}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="mt-5 space-y-3">
                      {businessHealth.categories.map(category => (
                        <div key={category.key} className="rounded-lg border border-gray-100 p-3">
                          <div className="flex items-center justify-between text-sm font-medium text-gray-700">
                            <span>{category.label}</span>
                            <span>{category.score ? `${category.score}%` : '—'}</span>
                          </div>
                          <div className="mt-2 h-2 w-full rounded-full bg-gray-100">
                            <div
                              className={`h-full rounded-full ${
                                category.score ? getScoreBgColor(category.score) : 'bg-gray-300'
                              }`}
                              style={{ width: `${category.score || 0}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="rounded-xl border border-dashed border-gray-200 p-4 text-sm text-gray-500">
                    No business health assessment submitted.
                  </div>
                )}
              </motion.div>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="rounded-2xl border border-gray-200 bg-white/90 p-6 shadow-sm backdrop-blur">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-black">Coach Narrative</h3>
                  <HiSparkles className="h-5 w-5 text-amber-500" />
                </div>
                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <div className="mb-3 flex items-center gap-2 text-green-700">
                      <HiCheckCircle className="h-4 w-4" />
                      <p className="text-sm font-semibold">Key Achievements</p>
                    </div>
                    {report.summary.keyAchievements.length > 0 ? (
                      <ul className="space-y-2 text-sm text-gray-700">
                        {report.summary.keyAchievements.map((achievement, index) => (
                          <li key={index} className="rounded-lg bg-green-50 px-3 py-2">
                            {achievement}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-500">
                        No achievements captured for this period.
                      </p>
                    )}
                  </div>
                  <div>
                    <div className="mb-3 flex items-center gap-2 text-amber-700">
                      <HiClock className="h-4 w-4" />
                      <p className="text-sm font-semibold">Areas for Attention</p>
                    </div>
                    {report.summary.areasForAttention.length > 0 ? (
                      <ul className="space-y-2 text-sm text-gray-700">
                        {report.summary.areasForAttention.map((area, index) => (
                          <li key={index} className="rounded-lg bg-amber-50 px-3 py-2">
                            {area}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-500">No flagged risks this month.</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white/90 p-6 shadow-sm backdrop-blur">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-black">Operational Pulse</h3>
                  <HiDocumentChartBar className="h-5 w-5 text-blue-600" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-4">
                    <p className="text-xs uppercase tracking-wide text-gray-500">
                      Product submissions
                    </p>
                    <p className="text-3xl font-semibold text-gray-900">
                      {report.statistics.totalSubmissions}
                    </p>
                    <p className="text-xs text-gray-500">
                      {report.statistics.submissionsThisMonth} this month
                    </p>
                  </div>
                  <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-4">
                    <p className="text-xs uppercase tracking-wide text-gray-500">
                      Document uploads
                    </p>
                    <p className="text-3xl font-semibold text-gray-900">
                      {report.statistics.totalDocuments}
                    </p>
                    <p className="text-xs text-gray-500">
                      {report.statistics.documentsThisMonth} this month
                    </p>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="rounded-xl border border-gray-100 bg-white p-4">
                    <h4 className="text-sm font-semibold text-gray-800">Recent submissions</h4>
                    {report.productDevelopment.recentSubmissions.length > 0 ? (
                      <ul className="mt-3 space-y-3">
                        {report.productDevelopment.recentSubmissions
                          .slice(0, 4)
                          .map((submission, index) => (
                            <li
                              key={index}
                              className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
                            >
                              <p className="text-sm font-medium text-gray-900">
                                {submission.title || 'Untitled submission'}
                              </p>
                              <p className="text-xs text-gray-500">
                                {submission.stageName || submission.stage || 'Unknown stage'} •{' '}
                                {submission.createdAt}
                              </p>
                            </li>
                          ))}
                      </ul>
                    ) : (
                      <p className="mt-3 text-sm text-gray-500">No submissions logged yet.</p>
                    )}
                  </div>
                  <div className="rounded-xl border border-gray-100 bg-white p-4">
                    <h4 className="text-sm font-semibold text-gray-800">Recent documents</h4>
                    {report.documentBank.recentDocuments.length > 0 ? (
                      <ul className="mt-3 space-y-3">
                        {report.documentBank.recentDocuments.slice(0, 4).map((doc, index) => (
                          <li
                            key={index}
                            className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
                          >
                            <p className="text-sm font-medium text-gray-900">{doc.fileName}</p>
                            <p className="text-xs text-gray-500">
                              {doc.category || 'General'} • {doc.uploadedAt}
                            </p>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-3 text-sm text-gray-500">No documents uploaded yet.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Multiple Reports Display (for coaches) */}
        {reports.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Reports for {months[selectedMonth - 1]} {selectedYear}
            </h2>
            {reports.map((clientReport, index) => {
              const clientId = clientReport.reportMetadata.clientId;
              const cachedClient = clientLookup[clientId];
              const displayName =
                clientReport.clientInfo.name ||
                getClientDisplayName(cachedClient) ||
                getClientDisplayName(clientReport.clientInfo);
              const organizationLabel =
                clientReport.clientInfo.organization ||
                getOrganizationFromClient(cachedClient) ||
                clientReport.clientInfo.email;
              const badgeData = [
                { label: 'Commercialisation', value: clientReport.statistics?.readinessScore },
                { label: 'Go To Market', value: clientReport.statistics?.gtmProgress },
                { label: 'Startup Launch', value: clientReport.statistics?.launchScore },
                { label: 'Entrepreneur', value: clientReport.statistics?.entrepreneurScore },
                { label: 'Business Health', value: clientReport.statistics?.businessHealthScore },
              ];

              return (
                <motion.div
                  key={clientId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="rounded-2xl border border-gray-200 bg-white/90 p-6 shadow-sm backdrop-blur"
                >
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{displayName}</h3>
                      <p className="text-sm text-gray-500">{organizationLabel}</p>
                    </div>
                    <button
                      onClick={() => setReport(prepareReportForUI(clientReport))}
                      className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                    >
                      View Full Report
                    </button>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6">
                    <div>
                      <p className="text-xs text-gray-500">Readiness</p>
                      <p
                        className={`text-lg font-semibold ${getScoreColor(
                          clientReport.statistics.readinessScore
                        )}`}
                      >
                        {clientReport.statistics.readinessScore}%
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">GTM</p>
                      <p
                        className={`text-lg font-semibold ${getScoreColor(
                          clientReport.statistics.gtmProgress
                        )}`}
                      >
                        {clientReport.statistics.gtmProgress}%
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Launch</p>
                      <p
                        className={`text-lg font-semibold ${getScoreColor(
                          clientReport.statistics.launchScore || 0
                        )}`}
                      >
                        {clientReport.statistics.launchScore ?? 0}%
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Entrepreneur</p>
                      <p
                        className={`text-lg font-semibold ${getScoreColor(
                          clientReport.statistics.entrepreneurScore || 0
                        )}`}
                      >
                        {clientReport.statistics.entrepreneurScore ?? 0}%
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Business Health</p>
                      <p
                        className={`text-lg font-semibold ${getScoreColor(
                          clientReport.statistics.businessHealthScore || 0
                        )}`}
                      >
                        {clientReport.statistics.businessHealthScore ?? 0}%
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Submissions</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {clientReport.statistics.totalSubmissions}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Documents</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {clientReport.statistics.totalDocuments}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {badgeData.map(badge => (
                      <span
                        key={badge.label}
                        className="rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-gray-600"
                      >
                        {badge.label}: {typeof badge.value === 'number' ? `${badge.value}%` : '—'}
                      </span>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {!report && reports.length === 0 && !loading && (
          <div className="flex min-h-[400px] flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50">
            <HiDocumentChartBar className="mb-4 h-16 w-16 text-gray-400" />
            <h3 className="mb-2 text-xl font-semibold text-gray-700">No Report Generated</h3>
            <p className="text-gray-500">Select a month, year, and client to generate a report</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyCoachingReport;
